import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CardActionArea,
    CardMedia,
    Skeleton
} from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';

const TournamentSelector = ({ onSelect }) => {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadTournaments = () => {
            try {
                // Загружаем все файлы из директории TourJSON
                const tourData = [];
                const requireTournament = (id) => {
                    try {
                        const data = require(`./TourJSON/${id}`);
                        return {
                            id: id.replace('.json', ''),
                            ...data
                        };
                    } catch (e) {
                        return null;
                    }
                };

                // Получаем список всех JSON файлов в директории
                const requireContext = require.context('./TourJSON', false, /\.json$/);
                const files = requireContext.keys().map(key => key.replace('./', ''));
                
                files.forEach(file => {
                    const data = requireTournament(file);
                    if (data) {
                        tourData.push(data);
                    }
                });

                // Сортировка турниров
                tourData.sort((a, b) => {
                    if (a.mode === "Results" && b.mode !== "Results") return -1;
                    if (a.mode !== "Results" && b.mode === "Results") return 1;
                    if (a.mode === "Results" && b.mode === "Results") {
                        return new Date(a.completion_date) - new Date(b.completion_date);
                    }
                    if (a.mode === "Wait for Results" && b.mode === "Vote") return -1;
                    if (a.mode === "Vote" && b.mode === "Wait for Results") return 1;
                    return 0;
                });

                setTournaments(tourData);
            } catch (error) {
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, []);

    if (loading) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Grid container spacing={3}>
                    {[1, 2, 3].map((item) => (
                        <Grid item xs={12} md={4} key={item}>
                            <Card>
                                <Skeleton variant="rectangular" height={140} />
                                <CardContent>
                                    <Skeleton variant="text" height={40} />
                                    <Skeleton variant="text" />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (tournaments.length === 0) {
        return (
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, textAlign: 'center' }}>
                <Typography variant="h5" color="text.secondary">
                    Нет доступных турниров
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
                Выберите турнир
            </Typography>
            <Grid container spacing={3}>
                {tournaments.map((tournament) => (
                    <Grid item xs={12} md={4} key={tournament.id}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-8px)'
                                }
                            }}
                        >
                            <CardActionArea 
                                onClick={() => onSelect(tournament.id)}
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch'
                                }}
                            >
                                <CardMedia
                                    component="div"
                                    sx={{
                                        height: 140,
                                        backgroundColor: tournament.mode === "Vote" ? 'primary.main' : 
                                                      tournament.mode === "Results" ? 'success.main' : 'grey.500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}
                                >
                                    <TrophyIcon sx={{ fontSize: 60, color: 'white' }} />
                                </CardMedia>
                                <CardContent sx={{ 
                                    flexGrow: 1, 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 1
                                }}>
                                    <Typography 
                                        variant="h5" 
                                        component="h2" 
                                        sx={{
                                            mb: 1,
                                            minHeight: 64,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {tournament.tour}
                                    </Typography>
                                    <Box sx={{ mt: 'auto' }}>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{ mb: 1 }}
                                        >
                                            Статус: {tournament.mode === "Vote" ? "Открыто для голосования" :
                                                    tournament.mode === "Results" ? "Завершено" : "Ожидание"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Максимум команд: {tournament.max_team_count}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Очков доступно: {tournament.total_points}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {tournament.mode === "Results" && tournament.completion_date ? `Дата завершения: ${new Date(tournament.completion_date).toLocaleDateString()}` : 'Дата не указана'}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default TournamentSelector; 